import { Add, DocumentText } from 'iconsax-react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { EConsentMethod } from '@/pages/RunReport/types'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'
import { setConsent } from '@/store/api/RunReport/RunReport.slice'
import { fileToBase64 } from '@/utils/binary'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

const MAX_PDF_SIZE_BYTES = 6_000_000

export const ConsentRequestField = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const slice = useAppSelector(selectRunReport)

  const isUploaded =
    slice.useCases.consentSwitchValue &&
    slice.consent.method === EConsentMethod.Upload &&
    typeof slice.consent.file === 'string' &&
    slice.consent.file.length > 0

  const handleUpload = (acceptedFiles: File[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return
    fileToBase64(acceptedFiles[0], (base64File) => {
      dispatch(
        setConsent({
          ...slice.consent,
          method: EConsentMethod.Upload,
          file: base64File,
          fileName: acceptedFiles[0].name,
        }),
      )
    })
  }

  const handleUploadRejected = () => {
    toast.error(t('dashboard.runReport.consentRequired.fileError'))
  }

  const { getInputProps, open: openFilePicker } = useDropzone({
    onDrop: handleUpload,
    onDropRejected: handleUploadRejected,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: MAX_PDF_SIZE_BYTES,
    noClick: true,
  })

  if (!isUploaded) return null

  return (
    <>
      <div className="border-t border-neutralColor5" />
      <div className="flex flex-col gap-3">
        <input {...getInputProps()} />

        <div className="flex items-center justify-between gap-4">
          <p className="text-base font-medium text-neutralColor lg:text-xl">
            {t('dashboard.runReport.consentRequest.title')}
          </p>
          <Button
            type="button"
            variant="secondary"
            className="gap-2"
            onClick={openFilePicker}
          >
            {t('dashboard.runReport.consentRequest.uploadButton')}
            <Add size="16" color="currentColor" />
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full border border-neutralColor5">
            <DocumentText
              size="20"
              color={weCheckPalette.neutralColor60}
              variant="Bold"
            />
          </div>
          <p className="flex-1 truncate text-sm text-dynamicBlue">
            {slice.consent.fileName ||
              t('dashboard.runReport.consentRequired.uploadCardFileLoaded')}
          </p>
        </div>
      </div>
    </>
  )
}
